// Active wind session card — mirrors legacy renderWindCard exactly.
// Net PnL updates in-place via useLivePrice (no polling flicker).
import { useNavigate } from "react-router-dom";
import type { WindSessionWithLatest } from "@/types/api";
import { useWindLivePnl } from "@/hooks/use-wind-live-pnl";
import { fmt, fmtDur } from "@/lib/format";
import Badge from "@/components/common/badge";
import AnomalyBadge from "@/components/common/anomaly-badge";
import { useWindSessionAnomalies } from "@/hooks/use-anomalies";
import SessionDirectionBadge from "@/components/wind/session-direction-badge";

interface WindCardProps {
  row: WindSessionWithLatest;
}

export default function WindCard({ row }: WindCardProps) {
  const { session, latest_cycle, cycles_count, total_pnl } = row;
  const anomalies = useWindSessionAnomalies(session.id);
  const navigate = useNavigate();

  // Direction from latest cycle (may flip vs initial during chasing-maker)
  const dir = latest_cycle?.direction ?? session.direction_initial ?? "long";
  const cycleStatus = latest_cycle?.status ?? "pending";

  // Live net PnL via WS ticks
  const realizedPnl = total_pnl ?? 0;
  const { net: netVal } = useWindLivePnl(session, latest_cycle, realizedPnl);
  const netColor = netVal >= 0 ? "#22c55e" : "#ef4444";

  const cycleLabel =
    cycleStatus === "pending" && latest_cycle?.entry_limit_price
      ? "chasing maker"
      : cycleStatus;

  const cycleColor =
    cycleStatus === "open"
      ? "#22c55e"
      : cycleStatus === "cooldown"
      ? "#3b82f6"
      : "#eab308";

  // Show status badge only when not actively running / in cooldown
  const showStatusBadge =
    cycleStatus !== "open" &&
    cycleStatus !== "closing" &&
    cycleStatus !== "cooldown";

  return (
    <div
      className="bg-surface border border-border rounded-lg p-3 cursor-pointer hover:border-accent/40 transition-colors"
      onClick={() => navigate(`/wind/${session.id}`)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="flex items-baseline gap-1.5 min-w-0">
          <span className="font-semibold text-sm text-text truncate">
            {session.symbol}
          </span>
          <span className="text-[0.72rem] shrink-0">
            <span
              className="font-semibold"
              style={{ color: netColor }}
              data-wind-net={session.id}
            >
              {netVal >= 0 ? "+" : ""}
              {fmt(netVal, 4)}
            </span>
            <span className="text-muted hidden sm:inline">
              {" · "}
              {cycles_count} cycle{cycles_count === 1 ? "" : "s"}
            </span>
          </span>
        </span>

        {/* Badges: wrap to second line on mobile */}
        <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[50%] sm:max-w-none">
          <AnomalyBadge anomalies={anomalies} />
          <SessionDirectionBadge
            direction={dir}
            cycleStatus={cycleStatus}
            cooldownUntil={latest_cycle?.cooldown_until}
            closeReason={latest_cycle?.close_reason}
            pausedUntil={session.paused_until}
          />
          {session.hold_minutes ? (
            <Badge
              color="accent"
              title={`Hold window from cycle open before TP snaps to BE-taker (${session.hold_minutes}m)`}
            >
              {fmtDur(session.hold_minutes)}
            </Badge>
          ) : null}
          {session.force_minutes ? (
            <Badge
              color="danger"
              title={`Force market exit after this long from cycle open (${session.force_minutes}m)`}
            >
              {fmtDur(session.force_minutes)}
            </Badge>
          ) : null}
          {session.auto_reopen ? (
            <Badge color="info" title="Auto-reopens after cooldown">
              auto
            </Badge>
          ) : (
            <Badge color="muted" title="One-shot (no auto-reopen)">
              one-shot
            </Badge>
          )}
          {/* AI auto-act indicator — matches grid-card convention. Present
              whenever the session is opted into automatic execution so user
              can tell at a glance which sessions AI can mutate without a
              user click. AI PAUSED / AI SKIP labels for cooldown states are
              handled upstream by SessionDirectionBadge. */}
          {session.ai_auto_act === 1 && (
            <Badge color="info" title="AI auto-act enabled (direction override / cut / TP push / SL trail / pause applied automatically)">
              AI
            </Badge>
          )}
          {/* Recent AI intervention chip on latest cycle. Mirrors grid-card's
              AI:CUT / AI:PAUSE chip so wind + grid cards read the same.
              Only surfaced when the advice is fresh (< 30 min). */}
          {windAiLastAct(latest_cycle) && (
            <Badge color="accent" title={windAiLastActTitle(latest_cycle)}>
              {windAiLastAct(latest_cycle)}
            </Badge>
          )}
          {showStatusBadge && (
            <span className="text-[0.7rem] px-1.5 py-0.5 rounded bg-muted/20 text-muted uppercase">
              {cycleStatus}
            </span>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="text-[0.72rem] text-muted flex flex-wrap gap-x-1.5 gap-y-0.5">
        <span className="text-success">TP {session.tp_pct}%</span>
        <span>/</span>
        <span className="text-danger">SL {session.sl_pct}%</span>
        <span>·</span>
        <span className="font-semibold" style={{ color: "#a855f7" }}>
          {session.leverage}x
        </span>
        {/* Cycle status is already communicated by the AI SKIP / COOLDOWN /
            AI PAUSED / LONG / SHORT badge above. Only render the label here
            when it adds info the badge doesn't (e.g. "chasing maker" on
            pending with a live limit order). */}
        {cycleLabel === "chasing maker" && (
          <>
            <span>·</span>
            <span style={{ color: cycleColor }}>{cycleLabel}</span>
          </>
        )}
      </div>
    </div>
  );
}

// --- AI helpers (wind-card specific, kept inline) -----------------------
// Derive the most relevant "AI just did X" chip for the session card from
// the latest cycle snapshot. Priority order matches user-visible impact:
//   ai-cut close > tp_push > sl_trail > tp_tier (BE-T/M) > null
// Only fires when the backing advice is fresh (< 30 min) so a week-old
// closed cycle doesn't light up an AI chip on the session card.

function parseWindAdvice(cycle: { ai_advice?: string | null } | null | undefined): any | null {
  if (!cycle?.ai_advice) return null;
  try {
    const p = JSON.parse(cycle.ai_advice);
    const ts = p.triggered_at ? new Date(p.triggered_at).getTime()
             : p.at ? new Date(p.at).getTime() : 0;
    if (!ts || Date.now() - ts > 30 * 60_000) return null;
    return p;
  } catch {
    return null;
  }
}

function windAiLastAct(cycle: { ai_advice?: string | null; close_reason?: string | null; stage?: string | null } | null | undefined): string | null {
  if (!cycle) return null;
  // Terminal ai-cut close is the strongest signal — show even if advice
  // has aged slightly (the close_reason itself is the source of truth).
  if (cycle.close_reason === "ai-cut") return "AI:CUT";
  const a = parseWindAdvice(cycle);
  if (!a) return null;
  if (typeof a.tp_push_bp === "number" && a.tp_push_bp > 0) return "AI:TP↑";
  if (typeof a.sl_trail_bp === "number" && a.sl_trail_bp > 0) return "AI:SL";
  // tp_tier rendered as AI:BE-T / AI:BE-M only when the stage actually
  // reflects it — same freshness check as cycleAiOrigin in the table.
  if (a.tp_tier && (cycle.stage === "be-taker-armed" || cycle.stage === "be-maker-armed")) {
    return cycle.stage === "be-maker-armed" ? "AI:BE-M" : "AI:BE-T";
  }
  return null;
}

function windAiLastActTitle(cycle: { ai_advice?: string | null; close_reason?: string | null } | null | undefined): string {
  if (!cycle) return "";
  if (cycle.close_reason === "ai-cut") {
    const a = parseWindAdvice(cycle);
    const why = a?.cut_why?.trim() || a?.why?.trim();
    return `AI cut the last cycle${why ? ` — ${why}` : ""}`;
  }
  const a = parseWindAdvice(cycle);
  if (!a) return "";
  const bits: string[] = [];
  if (typeof a.tp_push_bp === "number" && a.tp_push_bp > 0) bits.push(`TP pushed +${a.tp_push_bp}bp`);
  if (typeof a.sl_trail_bp === "number" && a.sl_trail_bp > 0) bits.push(`SL trailed ${a.sl_trail_bp}bp`);
  if (a.tp_tier) bits.push(`TP tier ${a.tp_tier}`);
  const why = typeof a.why === "string" ? a.why : "";
  return `AI on live cycle: ${bits.join(" · ") || "intervened"}${why ? ` — ${why}` : ""}`;
}
